// Is this expense still owed?
//
// One function because three places ask (the payer cell, the row actions, and the
// modal that settles it) and they must never disagree — a row that shows "not yet
// paid" while offering no way to settle it, or the reverse, is worse than either.
//
// MySQL sends the flag back as 1/0 rather than a boolean, and rows recorded before
// the state existed (migration 040) come back paid. Both are handled here so no
// caller has to remember either.
export const isUnpaid = (expense) => expense.is_paid === 0 || expense.is_paid === false;

// WHO BEARS THE COST (migration 054) — the other axis, and a different question from
// the one above. `borne_by` is 'client', 'us', or NULL for a row recorded before anyone
// was asked. The three helpers name the three states so no caller writes the string
// literal, and above all so nothing treats "never asked" as "we absorb it": that
// conflation is the exact reason the column replaced a boolean.
export const isChargedOn = (expense) => expense.borne_by === 'client';
export const isAbsorbed = (expense) => expense.borne_by === 'us';
export const isBearerUnstated = (expense) => !expense.borne_by;

// HAS THE CHARGE ACTUALLY BEEN RAISED — which is a different question from who bears
// the cost, and the one that decides whether a client-borne row is a job to do or a job
// done. `borne_by = 'client'` is an INTENT recorded when the cost was entered;
// `charged_on_invoice_id` is the invoice that made it real (migration 052). Until one
// exists nobody owes us anything, which is why the two states must not look alike: one
// is waiting for somebody to raise it, the other is already on the money screen.
export const isCharged = (expense) => !!expense.charged_on_invoice_id;

// WHO FRONTED IT (migration 061) — a THIRD axis, and again a different question from
// both of the above. `payers` (069) is whose money left; this is who put their own
// money in ahead of the business, which is how a non-partner can pay for something at
// all: the secretary buying cakes for a client meeting.
//
// `wasFronted` is true whether or not they have been paid back, because it records who
// went to the shop and that stays true afterwards. `isAwaitingReimbursement` is the state
// that has consequences on screen — somebody is out of pocket right now — and it is the
// conjunction rather than either half, so a reimbursed expense stops being flagged the
// moment it is marked paid.
export const wasFronted = (expense) => !!expense.incurred_by_user_id;
export const isAwaitingReimbursement = (expense) => wasFronted(expense) && isUnpaid(expense);

// The state AFTER the money went back. Somebody fronted this cost and a partner has since
// paid them for it, which is one event recorded as marking the expense paid: the partner
// becomes the payer and the fronter stays on the row as the record of who actually went
// to the shop. The archive is the only screen that reads it — a paid row showing the
// payer alone is true and loses the half that explains why the payment happened.
export const wasReimbursed = (expense) => wasFronted(expense) && !isUnpaid(expense);
